import React, { useState, useCallback, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTheme } from '../src/contexts/ThemeContext';

// Fix Leaflet default icon paths broken by bundlers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

export interface LocationResult {
  direccion: string | null;
  colonia: string | null;
  ciudad: string | null;
  estado: string | null;
  codigoPostal: string | null;
  urlGoogleMaps: string | null;
  lat: number;
  lng: number;
}

interface MapLocationPickerProps {
  onSelect: (location: LocationResult) => void;
  onClose: () => void;
  initialLat?: number;
  initialLng?: number;
}

const FlyToPoint: React.FC<{ center: [number, number] }> = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    map.flyTo(center, map.getZoom(), { duration: 0.8 });
  }, [center[0], center[1]]);
  return null;
};

const ClickHandler: React.FC<{ onClick: (lat: number, lng: number) => void }> = ({ onClick }) => {
  useMapEvents({
    click(e) {
      onClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

async function reverseGeocode(lat: number, lng: number): Promise<Omit<LocationResult, 'lat' | 'lng' | 'urlGoogleMaps'>> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&accept-language=es`,
      { headers: { 'User-Agent': 'DeliveryApp/1.0' } }
    );
    const data = await res.json();
    const addr = data.address || {};

    const road = addr.road || addr.pedestrian || addr.footway || '';
    const houseNumber = addr.house_number || '';
    const direccion = [road, houseNumber].filter(Boolean).join(' ') || null;

    const colonia = addr.suburb || addr.neighbourhood || addr.hamlet || null;
    const ciudad = addr.city || addr.town || addr.village || addr.municipality || null;
    const estado = addr.state || null;
    const codigoPostal = addr.postcode || null;

    return { direccion, colonia, ciudad, estado, codigoPostal };
  } catch {
    return { direccion: null, colonia: null, ciudad: null, estado: null, codigoPostal: null };
  }
}

const MapLocationPicker: React.FC<MapLocationPickerProps> = ({ onSelect, onClose, initialLat, initialLng }) => {
  const { theme } = useTheme();
  const defaultLat = initialLat ?? 19.4326;
  const defaultLng = initialLng ?? -99.1332;

  const [markerPos, setMarkerPos] = useState<[number, number]>([defaultLat, defaultLng]);
  const [flyTarget, setFlyTarget] = useState<[number, number]>([defaultLat, defaultLng]);
  const [geocodeResult, setGeocodeResult] = useState<Omit<LocationResult, 'lat' | 'lng' | 'urlGoogleMaps'> | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handlePositionChange = useCallback(async (lat: number, lng: number) => {
    setMarkerPos([lat, lng]);
    setFlyTarget([lat, lng]);
    setLoading(true);
    const result = await reverseGeocode(lat, lng);
    setGeocodeResult(result);
    setLoading(false);
  }, []);

  const handleConfirm = () => {
    const [lat, lng] = markerPos;
    const urlGoogleMaps = `https://www.google.com/maps?q=${lat},${lng}`;
    onSelect({
      lat,
      lng,
      urlGoogleMaps,
      direccion: geocodeResult?.direccion ?? null,
      colonia: geocodeResult?.colonia ?? null,
      ciudad: geocodeResult?.ciudad ?? null,
      estado: geocodeResult?.estado ?? null,
      codigoPostal: geocodeResult?.codigoPostal ?? null,
    });
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1&accept-language=es`,
        { headers: { 'User-Agent': 'DeliveryApp/1.0' } }
      );
      const data = await res.json();
      if (data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lng = parseFloat(data[0].lon);
        await handlePositionChange(lat, lng);
      }
    } catch { /* ignore */ }
    setSearching(false);
  };

  const handleLocateMe = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        await handlePositionChange(pos.coords.latitude, pos.coords.longitude);
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const markerEventHandlers = {
    dragend: (e: any) => {
      const latlng = e.target.getLatLng();
      handlePositionChange(latlng.lat, latlng.lng);
    },
  };

  // Reverse geocode initial position
  useEffect(() => {
    handlePositionChange(defaultLat, defaultLng);
  }, []);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4">
      <div className="relative w-full max-w-2xl rounded-2xl bg-white dark:bg-slate-800 shadow-xl flex flex-col max-h-[90vh] overflow-hidden" style={{ animation: 'crecer 0.3s ease-out' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-[#307CAF]">location_on</span>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Seleccionar ubicación</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors">
            <span className="material-symbols-outlined text-gray-500 dark:text-gray-400">close</span>
          </button>
        </div>

        {/* Search bar */}
        <div className="px-5 py-3 flex gap-2">
          <div className="flex-1 relative">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg">search</span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Buscar dirección o lugar..."
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#307CAF]"
            />
          </div>
          <button
            onClick={handleSearch}
            disabled={searching}
            className="px-3 py-2 rounded-lg bg-[#307CAF] text-white text-sm font-medium hover:bg-[#307CAF]/90 disabled:opacity-60 transition-colors"
          >
            {searching ? <span className="material-symbols-outlined animate-spin text-base">progress_activity</span> : 'Buscar'}
          </button>
          <button
            onClick={handleLocateMe}
            disabled={locating}
            title="Mi ubicación"
            className="px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60 transition-colors"
          >
            {locating ? <span className="material-symbols-outlined animate-spin text-base">progress_activity</span> : <span className="material-symbols-outlined text-base">my_location</span>}
          </button>
        </div>

        {/* Map */}
        <div 
          className="relative" 
          style={{ 
            height: 350,
            filter: theme === 'dark' ? 'invert(90%) hue-rotate(180deg) brightness(1.1) contrast(1.1)' : 'none'
          }}
        >
          <MapContainer
            center={[defaultLat, defaultLng]}
            zoom={15}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom
            zoomControl={false}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Marker position={markerPos} draggable eventHandlers={markerEventHandlers} />
            <ClickHandler onClick={handlePositionChange} />
            <FlyToPoint center={flyTarget} />
          </MapContainer>
        </div>

        {/* Result preview */}
        <div className="px-5 py-4 border-t border-gray-200 dark:border-slate-700">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <span className="material-symbols-outlined animate-spin text-base">progress_activity</span>
              Obteniendo dirección...
            </div>
          ) : geocodeResult ? (
            <div className="space-y-1 text-sm">
              <div className="flex items-start gap-2">
                <span className="material-symbols-outlined text-[#307CAF] text-base mt-0.5">pin_drop</span>
                <div className="text-slate-700 dark:text-slate-300">
                  <p className="font-medium">{[geocodeResult.direccion, geocodeResult.colonia].filter(Boolean).join(', ') || 'Sin dirección'}</p>
                  <p className="text-slate-500 dark:text-slate-400">
                    {[geocodeResult.ciudad, geocodeResult.estado, geocodeResult.codigoPostal].filter(Boolean).join(', ')}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500 dark:text-slate-400">Haz clic en el mapa o arrastra el marcador para seleccionar la ubicación.</p>
          )}

          <div className="flex justify-end gap-3 mt-3">
            <button onClick={onClose} className="rounded-lg border border-gray-200 dark:border-slate-600 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
              Cancelar
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading || !geocodeResult}
              className="inline-flex items-center gap-2 rounded-lg bg-[#307CAF] px-4 py-2 text-sm font-semibold text-white shadow hover:bg-[#307CAF]/90 disabled:opacity-60 transition-colors"
            >
              <span className="material-symbols-outlined text-base">check</span>
              Confirmar ubicación
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapLocationPicker;
